import React, { useState, useEffect } from 'react';
import { Mail, Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import EmailRichTextEditor from '../../components/Admin/EmailEditor/EmailRichTextEditor';
import { getEmailTemplates, updateEmailTemplate } from '../../services/adminService';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  variables: string[];
  updated_at: string;
}

const AdminEmails: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ subject: '', body_html: '' });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getEmailTemplates();
      setTemplates(data);
      if (data.length > 0 && !selectedId) {
        handleSelectTemplate(data[0]);
      }
    } catch (err) {
      console.error('Failed to load email templates', err);
      setMessage({ type: 'error', text: 'Failed to load email templates' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleSelectTemplate = (template: EmailTemplate) => {
    setSelectedId(template.id);
    setFormData({ subject: template.subject, body_html: template.body_html });
    setMessage(null);
  };

  const selectedTemplate = templates.find(t => t.id === selectedId);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId) return;

    setSaving(true);
    setMessage(null);
    try {
      await updateEmailTemplate(selectedId, formData);
      setMessage({ type: 'success', text: 'Template saved successfully' });
      // Update local state without full reload
      setTemplates(prev => prev.map(t => t.id === selectedId ? { ...t, ...formData } : t));
    } catch (err: any) {
      console.error('Failed to save template', err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Mail className="h-6 w-6 text-indigo-600" />
          Email Templates
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage the automated emails sent to users (e.g., credentials, upgrades).
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar: Template List */}
        <div className="lg:col-span-1 border-r border-gray-200 pr-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Templates</h2>
            <button onClick={loadTemplates} className="text-gray-400 hover:text-indigo-600">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <ul className="space-y-2">
            {templates.map(template => (
              <li key={template.id}>
                <button
                  onClick={() => handleSelectTemplate(template)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    selectedId === template.id
                      ? 'bg-indigo-50 text-indigo-700 border border-indigo-100'
                      : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  {template.name}
                </button>
              </li>
            ))}
            {templates.length === 0 && !loading && (
              <li className="text-sm text-gray-500 py-2">No templates found. Run the database migration script.</li>
            )}
          </ul>
        </div>

        {/* Main Area: Editor */}
        <div className="lg:col-span-3">
          {selectedTemplate ? (
            <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{selectedTemplate.name}</h2>
                  <p className="text-xs text-gray-500 mt-1">ID: {selectedTemplate.id}</p>
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save Changes
                </button>
              </div>

              <div className="p-6 space-y-6">
                {message && (
                  <div className={`p-4 rounded-lg flex items-center gap-3 text-sm ${
                    message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                    {message.text}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Subject</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={e => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Body</label>
                  <EmailRichTextEditor
                    contentKey={selectedTemplate.id}
                    bodyHtml={formData.body_html}
                    subject={formData.subject}
                    variables={selectedTemplate.variables}
                    onBodyHtmlChange={html => setFormData(prev => ({ ...prev, body_html: html }))}
                  />
                </div>
              </div>
            </form>
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-gray-50 rounded-xl border border-dashed border-gray-300 text-gray-500">
              {loading ? (
                <><RefreshCw className="h-8 w-8 animate-spin mb-4 text-indigo-400" /> Loading templates...</>
              ) : (
                <><Mail className="h-10 w-10 text-gray-300 mb-4" /> Select a template to edit</>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmails;
